import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "@/app/hooks";
import { fetchApplicationById } from "@/features/applicant/applicationSlice";
import {
  isVerifastIdentityFailure,
  shouldStopVerifastPolling,
  type VerifastStatus,
} from "@/features/applicant/utils/verifastStatus";
import apiClient from "@/lib/api/apiClient";
import {
  trackCompleteIdentityVerification,
  trackFailedIdentityVerification,
} from "@/lib/utils/analytics";
import { showApiError, showError, showInfo } from "@/lib/utils/notifications";

export type VerifastConfig = {
  VERIFAST_ENABLED?: string;
  VERIFAST_ID_ONLY?: string;
  VERIFAST_APPLICANT_HOST?: string;
};

export type { VerifastStatus };

function isFlagOn(value?: string | null) {
  return ["1", "true", "yes", "on"].includes(
    String(value || "").toLowerCase().trim(),
  );
}

export function useVerifastIdentity(
  applicantId?: string | number | null,
  isReadOnly = false,
) {
  const dispatch = useAppDispatch();
  const [config, setConfig] = useState<VerifastConfig | null>(null);
  const [status, setStatus] = useState<VerifastStatus | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [statusReady, setStatusReady] = useState(false);
  const notifyCompleteRef = useRef(false);
  /** True after the user submits Verifast, until a final success or failure is recorded. */
  const awaitingResultRef = useRef(false);

  const enabled = isFlagOn(config?.VERIFAST_ENABLED);
  const rawStatus = String(status?.id_verify_status || "pending").toLowerCase();
  const identityFailed = isVerifastIdentityFailure(status);
  // Status reflects stored DB/session data even when Verifast config is later turned off.
  const isComplete = statusReady && rawStatus === "complete";
  const isFailed = statusReady && identityFailed;
  const isNeedsReview =
    statusReady && !identityFailed && rawStatus === "needs_review";
  const isInProgress =
    statusReady && !identityFailed && rawStatus === "in_progress";
  const hasStoredSession =
    statusReady &&
    (!!status?.session_id ||
      ["complete", "failed", "needs_review", "in_progress"].includes(rawStatus));
  /** Config off but applicant already started Verifast — allow resume, not new enrollments. */
  const canUseVerifast = enabled || hasStoredSession;
  const canStart =
    canUseVerifast &&
    statusReady &&
    !isComplete &&
    !isFailed &&
    !isNeedsReview &&
    !isReadOnly &&
    !showPopup;

  const refreshStatus = useCallback(async (): Promise<VerifastStatus | null> => {
    if (!applicantId) return null;

    const response = await apiClient.get<VerifastStatus>(
      `/api/verifast/status/${applicantId}`,
    );
    setStatus(response.data);
    setStatusReady(true);

    const idStatus = String(response.data?.id_verify_status || "").toLowerCase();

    if (idStatus === "complete") {
      await dispatch(fetchApplicationById(Number(applicantId))).unwrap();
      if (notifyCompleteRef.current) {
        notifyCompleteRef.current = false;
        showInfo("ID verification was successful. You may continue.");
      }
      if (awaitingResultRef.current) {
        awaitingResultRef.current = false;
        trackCompleteIdentityVerification();
      }
    } else if (
      idStatus === "needs_review" ||
      idStatus === "failed" ||
      isVerifastIdentityFailure(response.data)
    ) {
      notifyCompleteRef.current = false;
      // session.rejected is not a final failure; polling may still receive approval.
      if (awaitingResultRef.current && isVerifastIdentityFailure(response.data)) {
        awaitingResultRef.current = false;
        trackFailedIdentityVerification();
      }
    }

    return response.data;
  }, [applicantId, dispatch]);

  const pollStatus = useCallback(async () => {
    // Short early intervals catch session.approved that often follows session.rejected ~1s later.
    const pollDelaysMs = [0, 2000, 2000, 3000, 5000, 10000, 10000];
    for (const delayMs of pollDelaysMs) {
      if (delayMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      }
      const next = await refreshStatus();
      if (shouldStopVerifastPolling(next)) {
        return;
      }
    }
  }, [refreshStatus]);

  const handlePopupClose = useCallback(
    (reason: "manual" | "complete") => {
      setShowPopup(false);
      setIframeUrl(null);
      notifyCompleteRef.current = true;
      awaitingResultRef.current = true;
      showInfo(
        reason === "complete"
          ? "Identity verification submitted. Checking results..."
          : "Verification window closed. Checking results...",
      );
      void pollStatus();
    },
    [pollStatus],
  );

  const handleStepComplete = useCallback(() => {
    // Verifast UI shows success in the iframe; defer backend status check until the
    // popup closes so a transient session.rejected webhook does not flash failure
    // behind the modal before session.approved arrives.
    notifyCompleteRef.current = true;
    awaitingResultRef.current = true;
  }, []);

  const startVerification = useCallback(async () => {
    if (!applicantId || isReadOnly) return;

    try {
      setStarting(true);
      setIframeUrl(null);
      setShowPopup(false);

      const sessionResponse = await apiClient.post<{
        session_id?: string;
        status?: string;
        iframe_url?: string;
      }>(`/api/verifast/create_session/${applicantId}`, {});

      const url = sessionResponse.data?.iframe_url || null;
      if (sessionResponse.data?.status) {
        setStatus((prev) => ({
          ...(prev || {}),
          id_verify_status: sessionResponse.data.status || "pending",
          session_id: sessionResponse.data.session_id || prev?.session_id,
        }));
      }

      if (!url) {
        showError("Unable to load identity verification. Please try again.");
        return;
      }

      setIframeUrl(url);
      setShowPopup(true);
    } catch (error) {
      setShowPopup(false);
      setIframeUrl(null);
      showApiError(error);
    } finally {
      setStarting(false);
    }
  }, [applicantId, isReadOnly]);

  const refreshStatusRef = useRef(refreshStatus);
  refreshStatusRef.current = refreshStatus;

  useEffect(() => {
    setShowPopup(false);
    setIframeUrl(null);
  }, [applicantId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!applicantId) return;
      try {
        setConfigLoading(true);
        setStatusReady(false);
        const configResponse = await apiClient.get<VerifastConfig>(
          "/api/config/verifast",
        );
        if (cancelled) return;
        const nextConfig = configResponse.data || {};
        setConfig(nextConfig);
        try {
          await refreshStatusRef.current();
        } catch {
          if (!cancelled) {
            setStatus({ id_verify_status: "pending" });
            setStatusReady(true);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setStatusReady(true);
          showApiError(error);
        }
      } finally {
        if (!cancelled) {
          setConfigLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [applicantId]);

  return {
    enabled,
    canUseVerifast,
    statusReady,
    hasStoredSession,
    rawStatus,
    isComplete,
    isFailed,
    isNeedsReview,
    isInProgress,
    canStart,
    config,
    status,
    iframeUrl,
    showPopup,
    configLoading,
    starting,
    setStatus,
    startVerification,
    handlePopupClose,
    handleStepComplete,
  };
}
